import { apiClient } from './api.js';
import { unwrap } from './api-error.js';

/**
 * Krok KAMP2 — klient platformní adminu nad `/v1/admin/email-campaigns`.
 *
 * DTO jsou psané ručně (ne z generovaného `ApiPaths`), protože odpověď
 * `/v1/admin/email-campaigns` je složená z více tabulek a jeden generovaný
 * typ by tu byl jen alias na `unknown`.
 *
 * POZOR na jména cest: `/v1/admin/campaigns` od kroku PL1 obsahuje *jiné*
 * věci (pilotní kampaně konkrétní kanceláře). E-mailová kampaň platformy je
 * pod `/v1/admin/email-campaigns`.
 */

export interface FunnelStageDto {
  readonly stage: string;
  readonly count: number;
  readonly pct: number;
}

export interface EmailCampaignDto {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly wave: string;
  readonly dailyCapOverride: number | null;
  readonly domainDailyCapOverride: number | null;
  readonly statusReason: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly stoppedAt: string | null;
  readonly funnel: readonly FunnelStageDto[];
  readonly bounced: number;
  readonly complained: number;
  readonly unsubscribed: number;
}

export interface SendingDomainDto {
  readonly domain: string;
  readonly status: string;
  readonly warmupDays: number;
  readonly warmupFirstDayCap: number;
  readonly warmupDailyGrowth: number;
  readonly warmupStartedAt: string | null;
  readonly statusReason: string | null;
  readonly sentToday: number;
  /** Krok KAMP3 (zadání bod 5): role, váha, dnešní strop a stav brzdy. */
  readonly role: 'warm' | 'new';
  readonly weight: number;
  readonly dailyCapToday: number;
  readonly bouncePct: number;
  readonly complaintPct: number;
  readonly brakeEngaged: boolean;
}

/** Krok KAMP3 bod 4 — náhled e-mailu přesně tak, jak odejde. */
export interface EmailTemplatePreviewDto {
  readonly variant: 'A' | 'B' | 'C';
  readonly active: boolean;
  readonly subject: string;
  readonly text: string;
  readonly html: string | null;
  readonly headers: Readonly<Record<string, string>>;
}

export interface EmailCampaignOverview {
  readonly campaigns: readonly EmailCampaignDto[];
  readonly domains: readonly SendingDomainDto[];
  /** true = `REOS_OUTBOUND_SEND=on` (pojistka je otevřená). */
  readonly outboundSend: boolean;
}

export async function getEmailCampaignOverview(): Promise<EmailCampaignOverview> {
  return (await unwrap(apiClient.GET('/v1/admin/email-campaigns', {}))) as EmailCampaignOverview;
}

/** STOP — zastaví odesílání okamžitě, bez restartu workera. */
export async function stopEmailCampaign(id: string): Promise<{ status: string }> {
  return (await unwrap(
    apiClient.POST('/v1/admin/email-campaigns/{id}/stop', { params: { path: { id } } }),
  )) as { status: string };
}

/** Znovu spustit (jen po GO R.B.). */
export async function startEmailCampaign(id: string): Promise<{ status: string }> {
  return (await unwrap(
    apiClient.POST('/v1/admin/email-campaigns/{id}/start', { params: { path: { id } } }),
  )) as { status: string };
}

/** Ruční posun denního stropu (plán 50 → 100 → 250 → 500). */
export async function setEmailCampaignCap(
  id: string,
  dailyCap: number,
  domainDailyCap?: number,
): Promise<{ dailyCapOverride: number; domainDailyCapOverride: number | null }> {
  return (await unwrap(
    apiClient.POST('/v1/admin/email-campaigns/{id}/cap', {
      params: { path: { id } },
      body: { dailyCap, ...(domainDailyCap === undefined ? {} : { domainDailyCap }) },
    }),
  )) as { dailyCapOverride: number; domainDailyCapOverride: number | null };
}

/** Krok KAMP3 bod 4 — náhled e-mailu PŘESNĚ tak, jak odejde (patička, odhlášení). */
export async function getEmailCampaignTemplatePreview(
  campaignId: string,
  variant: 'A' | 'B' | 'C',
): Promise<EmailTemplatePreviewDto> {
  return (await unwrap(
    apiClient.GET('/v1/admin/email-campaigns/{id}/preview/{variant}', {
      params: { path: { id: campaignId, variant } },
    }),
  )) as EmailTemplatePreviewDto;
}

/**
 * Odkaz na export CSV. Jako ODKAZ (ne fetch), aby prohlížeč dokument rovnou
 * vykreslil a session cookie poslal sám — stejný vzor jako u nákladů.
 */
export function emailCampaignsCsvUrl(campaignId?: string): string {
  const search = new URLSearchParams();
  if (campaignId !== undefined) search.set('campaignId', campaignId);
  const base =
    (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_API_URL ??
    'http://localhost:3000';
  return `${base}/v1/admin/email-campaigns/export.csv${
    search.toString() === '' ? '' : `?${search.toString()}`
  }`;
}
